import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

let realClient: SupabaseClient | null = null;
if (isSupabaseConfigured) {
  realClient = createClient(supabaseUrl!, supabaseAnonKey!);
} else {
  if (typeof window !== 'undefined') {
    console.error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment.'
    );
  }
}

export const supabase = new Proxy({}, {
  get(_target, prop) {
    if (!realClient) {
      throw new Error('Supabase not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
    }
    // @ts-expect-error: dynamic property access on Supabase client
    return realClient[prop];
  }
}) as unknown as SupabaseClient;

// Database types
export interface Candidate {
  id: string;
  name: string;
  email: string;
  age: number;
  university: 'tsukuba' | 'chiba' | 'gunma';
  affiliation: string;
  department?: string;
  research_topic: string;
  research_overview: string;
  research_comment?: string;
  avatar_url?: string;
  has_collaborators: boolean;
  is_grad_student?: boolean;
  grad_program?: 'doctor' | 'master';
  grad_year?: number;
  key_slide_index: number;
  created_at: string;
  updated_at: string;
}

export interface SlideImage {
  id: string;
  candidate_id: string;
  slide_index: number;
  image_url: string;
  created_at: string;
}

export interface Evaluator {
  id: string;
  name: string;
  email: string;
  university?: 'tsukuba' | 'chiba' | 'gunma';
  affiliation?: string;
  department?: string;
  is_grad_student?: boolean;
  grad_program?: 'doctor' | 'master';
  grad_year?: number;
  avatar_url?: string;
  is_reviewer?: boolean;
  created_at: string;
}

export interface Evaluation {
  id: string;
  candidate_id: string;
  evaluator_id: string;
  rating: number;
  liked: boolean;
  want_to_participate: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminUser {
  id: string;
  user_id: string;
  created_at: string;
}

export interface EvaluationComment {
  id: string;
  evaluation_id: string;
  user_id: string;
  user_type: 'evaluator' | 'candidate';
  content: string;
  parent_comment_id: string | null;
  topic: string;
  author_name?: string;
  author_email?: string;
  created_at: string;
}

export interface NotificationPreference {
  id: string;
  user_email: string;
  notify_on_like: boolean;
  notify_on_comment: boolean;
  notify_on_reply: boolean;
  notify_on_rating: boolean;
  notify_on_participate: boolean;
  notify_interval_minutes: number;
  created_at: string;
  updated_at?: string;
}

// Extended types with relations
export interface CandidateWithSlides extends Candidate {
  slide_images: SlideImage[];
}

export interface CandidateWithEvaluations extends Candidate {
  slide_images: SlideImage[];
  evaluations: Evaluation[];
}
